import React from 'react';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Wand2, Lock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Special Effects — contextual sound effects that trigger on verse keywords
 * while the narration is playing (wind, waves, thunder, fire, dove…).
 *
 * Only relevant when TTS is on. Independent from the Ambient Scene.
 */
const SpecialEffectsPanel = ({
  effectsEnabled,
  onToggleEffects,
  effectVolume,
  onEffectVolumeChange,
  liveSync,
  onLiveSyncChange,
  isPremium,
  useRealSamples,
  onUseRealSamplesChange,
  es = false,
}) => (
  <div className="border-t border-muted pt-4" data-testid="special-effects-panel">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Wand2 className="w-4 h-4 text-primary" />
        <div className="flex flex-col">
          <span className="text-sm font-medium">{es ? 'Efectos especiales (con narración)' : 'Special effects (with narration)'}</span>
          <span className="text-xs text-muted-foreground">
            {es ? 'Se activan automáticamente con las palabras del versículo al leerlo' : "Auto-triggered by words in the verse as it's read aloud"}
          </span>
        </div>
      </div>
      <Switch
        checked={effectsEnabled}
        onCheckedChange={onToggleEffects}
        data-testid="effects-toggle"
      />
    </div>

    {effectsEnabled && (
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{es ? 'Volumen de efectos' : 'Effect volume'}</span>
            <span className="text-xs text-muted-foreground">{Math.round(effectVolume * 100)}%</span>
          </div>
          <Slider
            value={[effectVolume]}
            onValueChange={(v) => onEffectVolumeChange(v[0])}
            max={1}
            step={0.05}
            data-testid="effect-volume-slider"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm font-medium">{es ? 'Sincronización de palabras' : 'Live keyword sync'}</span>
            <span className="text-xs text-muted-foreground">
              {es ? 'Mezcla los efectos a medida que se pronuncia cada palabra' : 'Crossfade effects as each word is spoken'}
            </span>
          </div>
          <Switch
            checked={liveSync}
            onCheckedChange={onLiveSyncChange}
            data-testid="live-sync-toggle"
          />
        </div>

        <p className="text-xs text-muted-foreground italic">
          {es
            ? <>Palabras como <i>viento, tormenta, mar, fuego, paloma</i> activan efectos automáticamente.</>
            : <>Keywords like <i>wind, storm, sea, fire, dove</i> automatically trigger matching effects.</>}
        </p>

        <div className="flex items-center justify-between pt-2 border-t border-muted">
          <div className="flex flex-col">
            <span className="text-sm font-medium flex items-center gap-1.5">
              {!isPremium && <Lock className="w-3.5 h-3.5 text-amber-500" />}
              {es ? 'Muestras de calidad de estudio' : 'Studio-quality samples'}
            </span>
            <span className="text-xs text-muted-foreground">
              {isPremium
                ? (es ? 'Grabaciones reales para efectos y escenas' : 'Real field recordings for both effects and scenes')
                : (es ? 'Mejora a Premium para audio cinematográfico' : 'Upgrade to Premium for cinematic audio')}
            </span>
          </div>
          <Switch
            checked={useRealSamples && isPremium}
            onCheckedChange={(v) => {
              if (!isPremium) {
                toast.info(es ? 'Se requiere Premium — mejora en /pricing' : 'Premium required — upgrade at /pricing');
                return;
              }
              onUseRealSamplesChange(v);
            }}
            disabled={!isPremium}
            data-testid="real-samples-toggle"
          />
        </div>
      </div>
    )}
  </div>
);

export default SpecialEffectsPanel;
